;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import type { ErrorEvent } from '@sentry/nextjs';

type NetworkFailureEvent = Pick<ErrorEvent, 'exception' | 'fingerprint' | 'level' | 'tags'>;

interface NetworkFailureOptions {
  /** Overridable for tests; defaults to the browser's `navigator.onLine`. */
  isOffline?: boolean;
}

export const NETWORK_NOISE_CLASS = 'network';

/**
 * Chrome/Edge, Firefox, Safari and React Native/axios wording for a failed network request.
 * Anchored to the full message so `Failed to fetch dynamically imported module: ...` (a chunk
 * load failure) is not treated as a network failure. Sentry may append the failing host:
 * `Failed to fetch (b2b.nativeteams.com)`.
 */
const NETWORK_FAILURE_MESSAGE =
  /^(?:Failed to fetch|NetworkError when attempting to fetch resource\.?|Load failed|Network request failed)(?: \(([^()\s]+)\))?$/;

const UNKNOWN_HOST = 'unknown-host';

function isBrowserOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/**
 * Returns the host when the event is a network failure, otherwise null. Only the original
 * exception (the last value, since linked causes are prepended) is classified, so a real error
 * that wraps a network failure is never downgraded or dropped. The host comes from the message
 * suffix only; without it the failure cannot be reliably tied to a request, so it is grouped
 * under `unknown-host`.
 */
function getNetworkFailureHost(event: NetworkFailureEvent): string | null {
  const exception = event.exception?.values?.at(-1);
  if (exception?.type !== 'TypeError' || !exception.value) return null;

  const match = NETWORK_FAILURE_MESSAGE.exec(exception.value);
  if (!match) return null;

  return match[1] ?? UNKNOWN_HOST;
}

/**
 * Network failures (offline, DNS, CORS, blocked requests) are not actionable one by one, but a
 * spike on one host is a real outage signal. Keep them, grouped into one issue per host, tagged
 * `noise_class: network` and downgraded to `warning`, so the issue count stays small while event
 * counts stay accurate. Failures while the browser is offline carry no product signal and are
 * dropped.
 */
export function applyNetworkFailureHandling<T extends NetworkFailureEvent>(
  event: T,
  { isOffline = isBrowserOffline() }: NetworkFailureOptions = {}
): T | null {
  const host = getNetworkFailureHost(event);
  if (!host) return event;
  if (isOffline) return null;

  return {
    ...event,
    level: 'warning',
    fingerprint: ['network-failure', host],
    tags: { ...event.tags, noise_class: NETWORK_NOISE_CLASS, network_host: host },
  };
}
